import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { TextInput } from 'src/design/atoms/TextInput';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { ModalButton } from 'src/design/molecules/ModalButton';
import { ContactDto } from 'src/api/FiretrolApiClient';
import { useCreateContact } from 'src/api/queries/organization/useContactMutations';
import {
  FIELD_LIMITS,
  getErrorMessage,
  showToast,
  toFormikFieldErrors,
} from 'src/utils';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email()
    .required()
    .max(
      FIELD_LIMITS.singleLine,
      `Email must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Email'),
  firstName: Yup.string()
    .trim()
    .required()
    .max(
      FIELD_LIMITS.singleLine,
      `First Name must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('First Name'),
  lastName: Yup.string()
    .trim()
    .required()
    .max(
      FIELD_LIMITS.singleLine,
      `Last Name must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Last Name'),
  phone: Yup.string()
    .max(
      FIELD_LIMITS.singleLine,
      `Phone must be ${FIELD_LIMITS.singleLine} characters or fewer`,
    )
    .label('Phone'),
});

export interface IQuickCreateContactModalProps {
  /** Seeds the Email field; the address the user typed into the picker. */
  email: string;
  customerNumber: string;
  customerName: string;
  onCancel: () => void;
  onCreated: (contact: ContactDto) => void;
}

/**
 * @registry Confirmation dialog that creates a client contact from an address typed into ContactPicker.
 * Mount it only while a creation is pending — its form state is seeded on mount.
 */
export const QuickCreateContactModal: React.FC<
  IQuickCreateContactModalProps
> = ({ email, customerNumber, customerName, onCancel, onCreated }) => {
  const [error, setError] = React.useState<string | null>(null);
  const createContact = useCreateContact();

  const formik = useFormik({
    initialValues: { email, firstName: '', lastName: '', phone: '' },
    validationSchema,
    onSubmit: async (values, helpers) => {
      setError(null);
      const firstName = values.firstName.trim();
      const lastName = values.lastName.trim();
      const trimmedEmail = values.email.trim();
      try {
        const created = await createContact.mutateAsync({
          firstName,
          lastName,
          phone: values.phone.trim(),
          email: trimmedEmail,
          customerNumber,
          customerName,
        });
        // The API resolves a duplicate email on the same customer to the existing
        // contact instead of creating one, so the returned name can differ.
        if (
          (created.email ?? '').toLowerCase() === trimmedEmail.toLowerCase() &&
          (created.firstName !== firstName || created.lastName !== lastName)
        ) {
          showToast(
            `Selected existing contact ${created.firstName} ${created.lastName}`,
          );
        }
        onCreated(created);
      } catch (err: unknown) {
        for (const [field, message] of Object.entries(
          toFormikFieldErrors(err),
        )) {
          helpers.setFieldError(field, message);
        }
        setError(getErrorMessage(err));
      }
    },
  });

  const fieldError = (field: 'email' | 'firstName' | 'lastName' | 'phone') =>
    formik.touched[field] ? formik.errors[field] : undefined;

  return (
    <BaseModal
      opened
      onClose={onCancel}
      title="Create contact"
      size="md"
      centered
      closeButtonProps={{ 'aria-label': 'Close' }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack>
          <TextInput
            label="Customer"
            value={customerName || customerNumber}
            readOnly
            disabled
          />
          <TextInput
            label="Email"
            name="email"
            maxLength={FIELD_LIMITS.singleLine}
            withAsterisk
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={fieldError('email')}
          />
          <TextInput
            label="First Name"
            name="firstName"
            maxLength={FIELD_LIMITS.singleLine}
            withAsterisk
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={fieldError('firstName')}
          />
          <TextInput
            label="Last Name"
            name="lastName"
            maxLength={FIELD_LIMITS.singleLine}
            withAsterisk
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={fieldError('lastName')}
          />
          <TextInput
            label="Phone"
            name="phone"
            maxLength={FIELD_LIMITS.singleLine}
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={fieldError('phone')}
          />
          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <ModalButton
              type="button"
              onClick={onCancel}
              color="dark"
              variant="light"
            >
              Cancel
            </ModalButton>
            <ModalButton type="submit" loading={formik.isSubmitting}>
              Create contact
            </ModalButton>
          </Group>
        </Stack>
      </form>
    </BaseModal>
  );
};
